import { FormikProps } from 'formik'
import * as yup from 'yup'

import { TGenericAPIError } from '~/app/models'
import { TKnowledgeTableDataItem } from '~/features/knowledge/v1/models'
import { EChartsTimePeriod } from '~/features/overview/models'
import { TContact, TConversation } from '~/models/features'

export enum ECampaignListItemKeys {
  id = 'id',
  agentId = 'agentId',
  orgId = 'orgId',
  agentAccessScope = 'agentAccessScope',
  campaignAgentJunctions = 'CampaignAgentJunction',
  campaignName = 'campaignName',
  campaignType = 'campaignType',
  status = 'status',
  channel = 'channel',
  startDate = 'startDate',
  createdDate = 'createdDate',
  campaignObjective = 'campaignObjective',
  description = 'description',
  endDate = 'endDate',
  executionStatus = 'executionStatus',
  updatedDate = 'updatedDate',
  useAgentFacts = 'useAgentFacts',
  introMessage = 'introMessage',
  voicemailMessage = 'voicemailMessage',
  questions = 'questions',
  rules = 'rules',
  mergeFields = 'mergeFields',
  agentMetadataKeys = 'agentMetadataKeys',
  sharedCampaignAgentSettings = 'sharedCampaignAgentSettings',
  campaignFacts = 'campaignFacts',
  contactsCount = 'contactsCount',
  campaignProgress = 'campaignProgress',
  ofu_acceptsExternalContacts = 'ofu_acceptsExternalContacts',
  ofu_cadence = 'ofu_cadence',
  ofu_cadenceCustom = 'ofu_cadenceCustom',
  ofu_channels = 'ofu_channels',
  ofu_customEmailBody = 'ofu_customEmailBody',
  ofu_customEmailService = 'ofu_customEmailService',
  ofu_goal = 'ofu_goal',
  ofu_isManual = 'ofu_isManual',
  ofu_maxReachouts = 'ofu_maxReachouts',
  ofu_outreachTime = 'ofu_outreachTime',
  ofu_qualificationCriteria = 'ofu_qualificationCriteria',
  ofu_emailCsvFieldMapping = 'ofu_emailCsvFieldMapping',
  ofu_emailUploadEnabled = 'ofu_emailUploadEnabled',
  ofu_outboundEmailProvider = 'ofu_outboundEmailProvider',
  ofu_outboundEmailProviderId = 'ofu_outboundEmailProviderId',
  ofu_reportSenderEmail = 'ofu_reportSenderEmail',
  ofu_uploaderEmailAddress = 'ofu_uploaderEmailAddress',
}

export enum EOutreachReviewStatus {
  reviewed = 'reviewed',
  notReviewed = 'notReviewed',
}

export type TOutreachReviewStatus =
  | EOutreachReviewStatus.reviewed
  | EOutreachReviewStatus.notReviewed

export type TCampaignListItem = {
  [ECampaignListItemKeys.id]: string
  [ECampaignListItemKeys.agentId]: string | null
  [ECampaignListItemKeys.orgId]: string | null
  [ECampaignListItemKeys.agentAccessScope]: ECampaignAgentAccessScope | null
  [ECampaignListItemKeys.campaignAgentJunctions]:
    | TCampaignAgentJunction[]
    | null
  [ECampaignListItemKeys.campaignName]: string
  [ECampaignListItemKeys.status]: ECampaignStatus
  [ECampaignListItemKeys.channel]: ECampaignChannel
  [ECampaignListItemKeys.startDate]: string
  [ECampaignListItemKeys.campaignType]: string
  [ECampaignListItemKeys.createdDate]: string
  [ECampaignListItemKeys.campaignObjective]: string | null
  [ECampaignListItemKeys.description]: string | null
  [ECampaignListItemKeys.endDate]: string | null
  [ECampaignListItemKeys.executionStatus]: ECampaignExecutionStatus | null
  [ECampaignListItemKeys.updatedDate]: string | null
  [ECampaignListItemKeys.useAgentFacts]: boolean | null
  [ECampaignListItemKeys.introMessage]: string | null
  [ECampaignListItemKeys.voicemailMessage]: string | null
  [ECampaignListItemKeys.questions]: string[] | null
  [ECampaignListItemKeys.rules]: string[] | null
  [ECampaignListItemKeys.mergeFields]: TMergeField[] | null
  [ECampaignListItemKeys.agentMetadataKeys]: TMergeField[] | null
  [ECampaignListItemKeys.sharedCampaignAgentSettings]?: Record<
    string,
    TSharedCampaignAgentSetting
  > | null
  [ECampaignListItemKeys.campaignFacts]: TCampaignFact[] | null
  [ECampaignListItemKeys.ofu_acceptsExternalContacts]: boolean | null
  [ECampaignListItemKeys.ofu_cadence]: string | null
  [ECampaignListItemKeys.ofu_cadenceCustom]: string | null
  [ECampaignListItemKeys.ofu_channels]: ECampaignChannel[] | null
  [ECampaignListItemKeys.ofu_customEmailBody]:
    | string
    | Record<string, unknown>
    | null
  [ECampaignListItemKeys.ofu_customEmailService]: string | null
  [ECampaignListItemKeys.ofu_goal]: string | null
  [ECampaignListItemKeys.ofu_isManual]: boolean | null
  [ECampaignListItemKeys.ofu_maxReachouts]: number | null
  [ECampaignListItemKeys.ofu_outreachTime]: string | null
  [ECampaignListItemKeys.ofu_qualificationCriteria]: string | null
  [ECampaignListItemKeys.ofu_emailCsvFieldMapping]: Record<
    string,
    string | TCustomProperty[] | TAssistantRoutingConfig
  > | null
  [ECampaignListItemKeys.ofu_emailUploadEnabled]: boolean | null
  [ECampaignListItemKeys.ofu_outboundEmailProvider]: string | null
  [ECampaignListItemKeys.ofu_outboundEmailProviderId]: string | null
  [ECampaignListItemKeys.ofu_reportSenderEmail]: string | null
  [ECampaignListItemKeys.ofu_uploaderEmailAddress]: string | null
  [ECampaignListItemKeys.contactsCount]: number | null
  [ECampaignListItemKeys.campaignProgress]: number | null
}

export type TCampaignAgentJunction = {
  id?: string
  agentId: string
  campaignId?: string
  status?: ECampaignAgentJunctionStatus
  agentCampaignKnowledge?: TCampaignFact[] | null
  agentCampaignRules?: string | null
  agentMetadata?: Record<string, string | number | boolean | null> | null
  Agent?: {
    id: string
    agentReferenceName?: string | null
    agentName?: string | null
    businessName?: string | null
  } | null
}

export enum ECampaignTabs {
  details = 0,
  contacts = 1,
  results = 2,
}

export enum EOfuCampaignTabs {
  contacts = 0,
  performance = 1,
  assistants = 2,
  edit = 3,
}

export enum EOfuContactsTab {
  all = 'all',
  importHistory = 'import-history',
  add = 'add',
}

export enum EAddContactOptions {
  options = 'options',
  upload = 'upload',
  email = 'email',
  inbound = 'inbound',
  api = 'api',
  manually = 'manually',
  testPopulate = 'testPopulate',
  importConfigs = 'importConfigs',
}

export enum ECampaignExecutionStatus {
  notStarted = 'notStarted',
  inProgress = 'inProgress',
  /** Test campaigns run under this status; mirrors the campaign service enum. */
  inTesting = 'inTesting',
  completed = 'completed',
  paused = 'paused',
}

export enum EMessageStrategy {
  aiGenerated = 'aiGenerated',
  userDefined = 'userDefined',
}

export enum EStructuredTimingMode {
  relative = 'relative',
  absolute = 'absolute',
}

export enum EScheduleStepOffsetUnit {
  minutes = 'minutes',
  hours = 'hours',
  days = 'days',
  weeks = 'weeks',
}

export enum ECampaignScheduleStepKeys {
  id = 'id',
  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
  campaignId = 'campaignId',
  stepOrder = 'stepOrder',
  offsetAmount = 'offsetAmount',
  offsetUnit = 'offsetUnit',
  scheduleDate = 'scheduleDate',
  sendTime = 'sendTime',
  channel = 'channel',
  textMessage = 'textMessage',
  emailSubject = 'emailSubject',
  emailBody = 'emailBody',
  callScript = 'callScript',
  voicemailScript = 'voicemailScript',
  outreachInstructionsText = 'outreachInstructionsText',
}

export enum ECampaignKeys {
  agentId = 'agentId',
  orgId = 'orgId',
  agentAccessScope = 'agentAccessScope',
  campaignAgentJunctions = 'campaignAgentJunctions',
  sharedCampaignAgentIds = 'sharedCampaignAgentIds',
  sharedCampaignAgentSettings = 'sharedCampaignAgentSettings',
  agents = 'agents',
  description = 'description',
  messageStrategy = 'messageStrategy',
  campaignFacts = 'campaignFacts',
  campaignName = 'campaignName',
  campaignObjective = 'campaignObjective',
  campaignType = 'campaignType',
  /**
   * Derived, read-only, test campaigns only: how far the campaign's logical clock has
   * travelled. Advancing a test campaign in time stamps outreaches at their scheduled
   * time, so this can be in the future. Never sent on update.
   */
  campaignDate = 'campaignDate',
  channel = 'channel',
  createdDate = 'createdDate',
  executionStatus = 'executionStatus',
  id = 'id',
  introMessage = 'introMessage',
  mergeFields = 'mergeFields',
  agentMetadataKeys = 'agentMetadataKeys',
  questions = 'questions',
  rules = 'rules',
  outreachInstructionsText = 'outreachInstructionsText',
  startDate = 'startDate',
  endDate = 'endDate',
  status = 'status',
  updatedDate = 'updatedDate',
  useAgentFacts = 'useAgentFacts',
  voicemailMessage = 'voicemailMessage',
  // OFU specific fields
  ofuGoal = 'ofu_goal',
  ofuQualificationCriteria = 'ofu_qualificationCriteria',
  ofuOutreachTime = 'ofu_outreachTime',
  ofuIsManual = 'ofu_isManual',
  ofuMaxReachouts = 'ofu_maxReachouts',
  ofuCadence = 'ofu_cadence',
  ofuCadenceCustom = 'ofu_cadenceCustom',
  ofuChannels = 'ofu_channels',
  ofuCustomEmailService = 'ofu_customEmailService',
  ofuCustomEmailBody = 'ofu_customEmailBody',
  ofuAcceptsExternalContacts = 'ofu_acceptsExternalContacts',
  ofuOutboundEmailProvider = 'ofu_outboundEmailProvider',
  ofuOutboundEmailProviderId = 'ofu_outboundEmailProviderId',
  ofu_emailUploadEnabled = 'ofu_emailUploadEnabled',
  ofu_emailCsvFieldMapping = 'ofu_emailCsvFieldMapping',
  ofu_uploaderEmailAddress = 'ofu_uploaderEmailAddress',
  ofu_reportSenderEmail = 'ofu_reportSenderEmail',
  firstEmailBody = 'firstEmailBody',
  firstEmailSubject = 'firstEmailSubject',
  firstTextMessage = 'firstTextMessage',
  ofu_firstOutreachChannel = 'ofu_firstOutreachChannel',
  campaignTemplateId = 'campaignTemplateId',
  campaignTemplate = 'CampaignTemplate',
  campaignTemplateVersion = 'CampaignTemplateVersion',
  executionMode = 'executionMode',
  useStructuredSchedule = 'useStructuredSchedule',
  structuredTimingMode = 'structuredTimingMode',
  campaignScheduleSteps = 'CampaignScheduleStep',
}

export enum ECampaignType {
  simple = 'simple',
  ofu = 'ofu',
  ofuOneTime = 'ofuOneTime',
}

export enum EOfuOutboundEmailProvider {
  internal = 'internal',
  notSet = '',
}

export enum ECampaignTypeDisplay {
  simple = 'One Time(legacy)',
  ofu = 'Follow-up',
  ofuOneTime = 'One Time',
}

export type TCampaign = {
  [ECampaignKeys.agentId]: string
  [ECampaignKeys.orgId]?: string | null
  [ECampaignKeys.agentAccessScope]?: ECampaignAgentAccessScope
  [ECampaignKeys.campaignAgentJunctions]?: TCampaignAgentJunction[]
  [ECampaignKeys.sharedCampaignAgentIds]?: string[]
  [ECampaignKeys.sharedCampaignAgentSettings]?: Record<
    string,
    TSharedCampaignAgentSetting
  >
  [ECampaignKeys.agents]?: TCampaignAgentPayload[]
  [ECampaignKeys.campaignFacts]?: TCampaignFact[]
  [ECampaignKeys.campaignName]: string
  [ECampaignKeys.campaignObjective]: string
  [ECampaignKeys.campaignType]: ECampaignType
  /** Derived server-side for test campaigns only; absent otherwise. */
  [ECampaignKeys.campaignDate]?: string | null
  [ECampaignKeys.channel]: ECampaignChannel
  [ECampaignKeys.createdDate]: string
  [ECampaignKeys.executionStatus]: ECampaignExecutionStatus
  [ECampaignKeys.id]: string
  [ECampaignKeys.introMessage]?: string
  [ECampaignKeys.mergeFields]: TMergeField[]
  [ECampaignKeys.agentMetadataKeys]?: TMergeField[]
  [ECampaignKeys.questions]?: string[]
  [ECampaignKeys.rules]?: string[]
  [ECampaignKeys.outreachInstructionsText]?: string | null
  [ECampaignKeys.startDate]?: string
  [ECampaignKeys.endDate]?: string
  [ECampaignKeys.status]: ECampaignStatus
  [ECampaignKeys.updatedDate]: string
  [ECampaignKeys.useAgentFacts]?: boolean
  [ECampaignKeys.voicemailMessage]?: string
  // OFU specific fields - all optional to maintain backward compatibility
  [ECampaignKeys.ofuGoal]?: string
  [ECampaignKeys.ofuQualificationCriteria]?: string
  [ECampaignKeys.ofuOutreachTime]?: string
  [ECampaignKeys.ofuIsManual]?: boolean
  [ECampaignKeys.ofuMaxReachouts]?: number
  [ECampaignKeys.ofuCadence]?: string
  [ECampaignKeys.ofuCadenceCustom]?: string
  [ECampaignKeys.ofuChannels]?: ECampaignChannel[]
  [ECampaignKeys.ofuCustomEmailService]?: string
  [ECampaignKeys.ofuCustomEmailBody]?: string | Record<string, unknown>
  [ECampaignKeys.ofuAcceptsExternalContacts]?: boolean
  [ECampaignKeys.ofuOutboundEmailProvider]?: string
  [ECampaignKeys.ofuOutboundEmailProviderId]?: string
  [ECampaignKeys.ofu_emailUploadEnabled]?: boolean
  [ECampaignKeys.ofu_emailCsvFieldMapping]?: Record<
    string,
    string | TCustomProperty[] | TAssistantRoutingConfig
  >
  [ECampaignKeys.ofu_uploaderEmailAddress]?: string
  [ECampaignKeys.ofu_reportSenderEmail]?: string
  [ECampaignKeys.firstEmailBody]?: string
  [ECampaignKeys.firstEmailSubject]?: string
  [ECampaignKeys.firstTextMessage]?: string
  [ECampaignKeys.ofu_firstOutreachChannel]?: ECampaignChannel
  [ECampaignKeys.campaignTemplateId]?: string | null
  [ECampaignKeys.campaignTemplate]: Omit<
    TCampaignTemplate,
    'activeVersion' | 'campaignTemplateVersions' | 'status'
  > | null
  [ECampaignKeys.campaignTemplateVersion]?: TCampaignTemplateVersion | null
  [ECampaignKeys.messageStrategy]?: EMessageStrategy
  [ECampaignKeys.description]?: string
  [ECampaignKeys.executionMode]?: ECampaignExecutionMode | null
  [ECampaignKeys.useStructuredSchedule]?: boolean
  [ECampaignKeys.structuredTimingMode]?: EStructuredTimingMode
  [ECampaignKeys.campaignScheduleSteps]?: TCampaignScheduleStep[]
}

export type TCampaignCreatePayload = Omit<
  Partial<TCampaign>,
  ECampaignKeys.id | ECampaignKeys.createdDate | ECampaignKeys.updatedDate
>

export type TCampaignError = TGenericAPIError

export type TCampaignUpdatePayload = Omit<
  Partial<TCampaign>,
  | ECampaignKeys.createdDate
  | ECampaignKeys.agentId
  | ECampaignKeys.campaignAgentJunctions
> & {
  [ECampaignKeys.agentId]?: string | string[]
  [ECampaignKeys.campaignAgentJunctions]?: Array<{
    id: string
    status: ECampaignExecutionStatus
  }>
}

export type TCampaignFact = {
  id?: string
  text: string
}

export type TCampaignFormFields = Omit<
  TCampaign,
  | ECampaignKeys.agentId
  | ECampaignKeys.createdDate
  | ECampaignKeys.updatedDate
  | ECampaignKeys.questions
  | ECampaignKeys.outreachInstructionsText
  | ECampaignKeys.campaignFacts
  | ECampaignKeys.campaignTemplate
  | ECampaignKeys.campaignTemplateVersion
> & {
  [ECampaignKeys.questions]: string
  [ECampaignKeys.outreachInstructionsText]: string
  [ECampaignKeys.campaignFacts]: TKnowledgeTableDataItem[]
}

export type TDeleteCampaignContact = {
  campaignContactIds: string[]
}

export type TUpdateCampaignContact = {
  status?: ECampaignContactStatus
  contactMetadata?: Record<string, string>
  goalAchieved?: boolean
  sentiment?: ECampaignContactSentiment
}

export type TUpdateOutreachPayload = {
  status?: EOutreachStatus
  channel?: ECampaignChannel
  outcome?: string
  followUp?: string
  conversationId?: string
  eventId?: string
  emailBody?: string
  emailSubject?: string
  callScript?: string
  voicemailScript?: string
  textScript?: string
  reviewStatus?: TOutreachReviewStatus
}

export enum ECampaignContactListItemKeys {
  id = 'id',
  firstName = 'firstName',
  status = 'status',
  createdDate = 'createdDate',
  phone = 'phone',
  customVariables = 'customVariables',
  preview = 'preview',
  testContact = 'testContact',
}

export type TCampaignGetContactListItem = TCampaignGetContact & {
  [ECampaignContactListItemKeys.customVariables]: string
  [ECampaignContactListItemKeys.preview]: string
}

export enum EOptOutMessageBody {
  STOP = 'STOP',
  UNSUBSCRIBE = 'UNSUBSCRIBE',
  END = 'END',
  QUIT = 'QUIT',
  STOPALL = 'STOPALL',
  CANCEL = 'CANCEL',
  REVOKE = 'REVOKE',
  OPTOUT = 'OPTOUT',
}

export enum EOptInMessageBody {
  START = 'START',
  UNSTOP = 'UNSTOP',
}

export enum EOutreachStatus {
  pending = 'pending', // pending execution
  inProgress = 'inProgress', // we are waiting for execution to be completed - depending on the channel this may take a while
  failed = 'failed', // failed execution

  sent = 'sent', // for text: accepted by Twilio, awaiting a delivery receipt (DLR)
  delivered = 'delivered', // for email and text
  responded = 'responded', // for email and text
  read = 'read', // for email

  answered = 'answered', // for phone
  notAnswered = 'not_answered', // for phone or email if no replay was received
  voicemail = 'voicemail', // for phone
  busy = 'busy', // for phone
  noAnswer = 'no_answer', // for phone - this is from twillio
  noAnswer2 = 'no-answer', // for phone - this is from twillio
  cancelled = 'cancelled', // for phone - this is from twillio
  ringing = 'ringing', // for phone - this is from twillio
}

export type TOutreach = {
  id: string
  campaignContactId: string
  status: EOutreachStatus
  channel: ECampaignChannel
  outcome: string | null
  followUp: string | null
  conversationId: string | null
  eventId: string | null
  scheduledFor: Date | null
  executedAt: Date | null
  emailBody: string | null
  emailSubject: string | null
  callScript: string | null
  voicemailScript: string | null
  textScript: string | null
  conversation: TConversation | null
  reviewStatus?: TOutreachReviewStatus
  prompt?: string
  evaluationPrompt?: string
  friendlyExecutionError?: string
  detailedExecutionError?: string
  hasCustomerResponse?: boolean | null
}

export type TCampaignGetContact = {
  [ECampaignContactListItemKeys.id]: string
  [ECampaignContactListItemKeys.status]: ECampaignContactStatus | null
  campaignId: string | null
  contactId: string
  contactMetadata: Record<string, string> | null
  [ECampaignContactListItemKeys.createdDate]: string | null
  email: string | null
  executionDate: string | null
  executionError: string | null
  [ECampaignContactListItemKeys.firstName]: string | null
  lastName: string | null
  [ECampaignContactListItemKeys.phone]: string | null
  updatedDate: string | null
  contact: TContact | null
  Contact: TContact | null
  introMessage: string | null
  voicemailMessage: string | null
  testContact: boolean | null
  Outreach: TOutreach[] | null
  sourceType: EUploadLogSourceType | null
  agentName?: string | null
  numberOfMeaningfulReplies?: number | null
  goalAchieved?: boolean | null
  sentiment?: ECampaignContactSentiment | null
}

export type TCampaignUploadLog = {
  id: string
  receivedDate: string
  sourceType: string
  status: string
  orgId: string
  campaignId: string
  completedDate: string
  totalCount: number
  processedCount: number
  failedCount: number
  successCount: number
  csvName: string
  s3Uri: string
  error: string
  streamName: string
  outcomeDetails?: {
    action?: string | null
    integrationConfigId?: string | null
    addedCount?: number | null
    updatedCount?: number | null
    skippedCount?: number | null
    removedCount?: number | null
    reconciledMissingCount?: number | null
  } | null
}

export type TCampaignGetContactPayload = {
  campaignContacts: TCampaignGetContact[]
  filteredContactCount: number
  campaignContactsCount: number
}

export type TCampaignCreateContact = {
  agentId?: string
  phone?: string
  email?: string
  firstName?: string
  lastName?: string
  textOptedOut?: boolean
  emailUnsubscribed?: boolean
  status?: ECampaignContactStatus
  testContact?: boolean
  contactMetadata?: Record<string, string>
  sourceType?:
    | EUploadLogSourceType.uiCsvUpload
    | EUploadLogSourceType.uiManualUpload
  externalIdKey?: string
  externalIdValue?: string
}

export type TCampaignCreateContactBulkAsync = {
  contacts: TCampaignCreateContact[]
  action?: ECampaignUpsertAction
  sourceType?: EUploadLogSourceType
  processAsync?: boolean
  fileName?: string
  agentId?: string
}

export type TCampaignAnalytics = object

export type TMergeField = {
  id?: string
  friendlyName: string
  required: boolean
  variable: string | null
}

export enum ECampaignChannel {
  phone = 'phone',
  email = 'email',
  text = 'text',
}

export type TCampaignScheduleStep = {
  [ECampaignScheduleStepKeys.id]: string
  [ECampaignScheduleStepKeys.createdDate]?: string
  [ECampaignScheduleStepKeys.updatedDate]?: string
  [ECampaignScheduleStepKeys.campaignId]?: string
  [ECampaignScheduleStepKeys.stepOrder]: number
  [ECampaignScheduleStepKeys.offsetAmount]?: number | null
  [ECampaignScheduleStepKeys.offsetUnit]?: EScheduleStepOffsetUnit | null
  [ECampaignScheduleStepKeys.scheduleDate]?: string | null
  /** Minutes since midnight, 0..1439. `null` = Let AI decide. */
  [ECampaignScheduleStepKeys.sendTime]?: number | null
  [ECampaignScheduleStepKeys.channel]?: ECampaignChannel | null
  [ECampaignScheduleStepKeys.textMessage]?: string | null
  [ECampaignScheduleStepKeys.emailSubject]?: string | null
  [ECampaignScheduleStepKeys.emailBody]?: string | null
  [ECampaignScheduleStepKeys.callScript]?: string | null
  [ECampaignScheduleStepKeys.voicemailScript]?: string | null
  [ECampaignScheduleStepKeys.outreachInstructionsText]?: string | null
}

export type TReplaceCampaignScheduleStepPayload = Omit<
  TCampaignScheduleStep,
  | ECampaignScheduleStepKeys.id
  | ECampaignScheduleStepKeys.createdDate
  | ECampaignScheduleStepKeys.updatedDate
  | ECampaignScheduleStepKeys.campaignId
>

export type TReplaceCampaignScheduleStepsPayload = {
  timingMode: EStructuredTimingMode
  /** `null` clears all schedule steps on the BE. */
  steps: TReplaceCampaignScheduleStepPayload[] | null
}

/** Form representation until payload transform.
 * - `sendTime`: clock `HH:mm` string, or `null` when unset (e.g. minutes/hours wait)
 * - `channel`: `null` = Let AI decide, else pinned channel
 */
export type TCampaignScheduleStepForm = Omit<
  TCampaignScheduleStep,
  ECampaignScheduleStepKeys.sendTime
> & {
  [ECampaignScheduleStepKeys.sendTime]?: string | null
}

export const EActionVerb = {
  [ECampaignChannel.phone]: 'call',
  [ECampaignChannel.email]: 'send',
  [ECampaignChannel.text]: 'send',
} as const

export const getFriendlyChannelName = (channel: string): string => {
  switch (channel) {
    case ECampaignChannel.phone:
      return 'Phone'
    case ECampaignChannel.email:
      return 'Email'
    case ECampaignChannel.text:
      return 'Text'
    default:
      return channel || 'Unspecified'
  }
}

export const getChannelSortOrder = (channel: string): number => {
  const channelOrder = [
    ECampaignChannel.phone,
    ECampaignChannel.email,
    ECampaignChannel.text,
  ]
  return channelOrder.indexOf(channel as ECampaignChannel) || channel.length
}

export const getFriendlyChannelsNames = (
  channels: ECampaignChannel | string[] | undefined,
): string => {
  if (Array.isArray(channels)) {
    return (
      channels
        .map((channel) => getFriendlyChannelName(channel as ECampaignChannel))
        .join(', ') || 'No channels'
    )
  }

  return getFriendlyChannelName(channels as ECampaignChannel)
}

export enum ECampaignStatus {
  draft = 'draft',
  open = 'open',
  closed = 'closed',
  test = 'test',
  archived = 'archived',
}

export enum ECampaignAgentJunctionStatus {
  notStarted = 'notStarted',
  started = 'started',
}

export const getFriendlyStatusName = (status: ECampaignStatus): string => {
  switch (status) {
    case ECampaignStatus.draft:
      return 'Draft'
    case ECampaignStatus.open:
      return 'Open'
    case ECampaignStatus.closed:
      return 'Closed'
    case ECampaignStatus.test:
      return 'Test'
    case ECampaignStatus.archived:
      return 'Archived'
    default:
      return status || 'Unspecified'
  }
}

export enum ECampaignContactStatus {
  new = 'new',
  called = 'called',
  failed = 'failed',
  completed = 'completed',
  done = 'done',
  started = 'started',
  notStarted = 'notStarted',
  disabled = 'disabled',
  disqualified = 'disqualified',
  needsEvaluation = 'needsEvaluation',
  planning = 'planning',
  executing = 'executing',
  paused = 'paused',
  needsApproval = 'needsApproval',
}

export const WORKING_STATUSES = [
  ECampaignContactStatus.planning,
  ECampaignContactStatus.executing,
]

export const COMPLETED_STATUSES = [
  ECampaignContactStatus.completed,
  ECampaignContactStatus.done,
  ECampaignContactStatus.disqualified,
]

export const CONTACT_STATUS_LABELS: Record<ECampaignContactStatus, string> = {
  [ECampaignContactStatus.new]: 'Planning',
  [ECampaignContactStatus.called]: 'Called',
  [ECampaignContactStatus.failed]: 'Failed',
  [ECampaignContactStatus.completed]: 'Completed',
  [ECampaignContactStatus.done]: 'Done',
  [ECampaignContactStatus.started]: 'Awaiting execution',
  [ECampaignContactStatus.notStarted]: 'Not Started',
  [ECampaignContactStatus.disabled]: 'Paused',
  [ECampaignContactStatus.disqualified]: 'Disqualified',
  [ECampaignContactStatus.needsEvaluation]: 'Awaiting evaluation',
  [ECampaignContactStatus.needsApproval]: 'Awaiting approval',
  [ECampaignContactStatus.planning]: 'Planning',
  [ECampaignContactStatus.executing]: 'Executing',
  [ECampaignContactStatus.paused]: 'Campaign Paused',
}

export type TCampaignContactsFilterState = {
  contactStatusFilter: string[]
  outreachFilter: string[]
  sourceTypeFilter: string[]
  assistantFilter: string[]
  goalFilter: string[]
  sentimentFilter: string[]
}

export const DEFAULT_CAMPAIGN_CONTACTS_FILTER_STATE: TCampaignContactsFilterState =
  {
    contactStatusFilter: [],
    outreachFilter: [],
    sourceTypeFilter: [],
    assistantFilter: [],
    goalFilter: [],
    sentimentFilter: [],
  }

export enum ECampaignUpsertAction {
  upsert = 'upsert',
  remove = 'remove',
}

export const getFriendlyContactStatusName = (status: string): string => {
  return (
    CONTACT_STATUS_LABELS[status as ECampaignContactStatus] ||
    status ||
    'Unspecified'
  )
}

export const getContactStatusSortOrder = (status: string): number => {
  const statusOrder = [
    ECampaignContactStatus.called,
    ECampaignContactStatus.failed,
    ECampaignContactStatus.completed,
    ECampaignContactStatus.started,
    ECampaignContactStatus.planning,
    ECampaignContactStatus.executing,
    ECampaignContactStatus.needsEvaluation,
    ECampaignContactStatus.notStarted,
    ECampaignContactStatus.disabled,
    ECampaignContactStatus.disqualified,
  ]
  return statusOrder.indexOf(status as ECampaignContactStatus) || status.length
}

export type TCampaignOutletContext = {
  formik: FormikProps<TCampaignFormFields>
  showSaveAndTestDialog: boolean
  setShowSaveAndTestDialog: (shouldNavigate: boolean) => void
}

export type TVariableToColumnMap = {
  variable: string
  column: string | undefined
}

export type TAddContactResponse = {
  data: TCampaignGetContact[]
  message: string
  errors: {
    rowIndex: number
    message: string
  }[]
}

export type TAutoParsedColumnTitle = {
  columnTitle: string
  dbFieldConventionalName: string
}

export type TExecuteCampaignResponse = {
  message: string
}

export enum ESaveAndTestForm {
  campaignContactId = 'campaignContactId',
  firstName = 'firstName',
  phone = 'phone',
  mergeFields = 'mergeFields',
  email = 'email',
}

export type TMergeFieldWithValue = TMergeField & {
  value: string
}

export type TSaveAndTestForm = {
  [ESaveAndTestForm.campaignContactId]: string
  [ESaveAndTestForm.firstName]: string
  [ESaveAndTestForm.phone]: string
  [ESaveAndTestForm.mergeFields]: TMergeFieldWithValue[]
  [ESaveAndTestForm.email]?: string
}

export type TOFUCampaignFormFields = Pick<
  TCampaign,
  | ECampaignKeys.id
  | ECampaignKeys.campaignName
  | ECampaignKeys.campaignType
  | ECampaignKeys.channel
  | ECampaignKeys.mergeFields
  | ECampaignKeys.status
  | ECampaignKeys.ofuGoal
  | ECampaignKeys.ofuQualificationCriteria
  | ECampaignKeys.startDate
  | ECampaignKeys.endDate
  | ECampaignKeys.ofuOutreachTime
  | ECampaignKeys.ofuIsManual
  | ECampaignKeys.ofuMaxReachouts
  | ECampaignKeys.ofuCadence
  | ECampaignKeys.ofuChannels
  | ECampaignKeys.ofuCadenceCustom
  | ECampaignKeys.ofuCustomEmailService
  | ECampaignKeys.ofuCustomEmailBody
  | ECampaignKeys.outreachInstructionsText
  | ECampaignKeys.useAgentFacts
  | ECampaignKeys.ofuOutboundEmailProvider
  | ECampaignKeys.ofuOutboundEmailProviderId
  | ECampaignKeys.introMessage
  | ECampaignKeys.voicemailMessage
  | ECampaignKeys.firstEmailBody
  | ECampaignKeys.firstEmailSubject
  | ECampaignKeys.firstTextMessage
  | ECampaignKeys.ofu_firstOutreachChannel
  | ECampaignKeys.messageStrategy
> & {
  [ECampaignKeys.campaignFacts]: TKnowledgeTableDataItem[]
}

export type TOfuContact = {
  id: string
  agentId?: string
  assistantName?: string
  firstName: string
  lastName: string
  name: string
  email: string
  phone: string
  status: string
  statusReason?: string | null
  campaignId: string
  Outreach: TOutreach[]
  contact: TContact | null
  contactMetadata: Record<string, string>
  testContact?: boolean
  createdDate: string
  sourceType: EUploadLogSourceType
  numberOfMeaningfulReplies?: number
  goalAchieved?: boolean | null
  sentiment?: ECampaignContactSentiment | null
}

export type TContactRowProps = {
  contact: TOfuContact
  manualReview: boolean
  isDisqualified: (id: string) => boolean
}

export type TCampaignContactsTableProps = {
  contacts: TOfuContact[]
  manualReview: boolean
  isLoading?: boolean
}

export type TCustomProperty = {
  id: string
  name: string
  column: string
  customName?: string
}

export type TCustomPropertyForm = {
  id: string
  name: string
  value: string
  customName?: string
}

export type TMappedContact = {
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  textOptedOut?: boolean
  emailUnsubscribed?: boolean
  [key: string]: string | boolean | undefined
}

export enum EAssistantRoutingMode {
  single = 'single',
  byColumn = 'byColumn',
  byKeyAndValueColumns = 'byKeyAndValueColumns',
}

export type TAssistantRoutingConfig = {
  mode: EAssistantRoutingMode
  agentId?: string
  externalIdKey?: string
  externalIdKeyColumn?: string
  externalIdValueColumn?: string
}

export const simulateOfuCampaignSchema = yup.object().shape({
  [ESaveAndTestForm.campaignContactId]: yup.string().optional(),
  [ESaveAndTestForm.firstName]: yup.string().required('First name is required'),
  [ESaveAndTestForm.phone]: yup.string().optional(),
  [ESaveAndTestForm.email]: yup.string().email('Invalid email').optional(),
  [ESaveAndTestForm.mergeFields]: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.string().required('Value is required'),
      }),
    )
    .required('Merge fields are required'),
})

export type TCreateOutreachPayload = {
  channel: ECampaignChannel
  emailBody: string
  emailSubject: string
  callScript: string
  voicemailScript: string
  textScript: string
}

export type TOfuSimulateMessage = {
  role: string
  content: string
  date?: string
}

export type TOfuSimulateOutreach = TOutreach & {
  messages: TOfuSimulateMessage[]
}

export type TOfuSimulateContact = {
  outreaches: TOfuSimulateOutreach[]
  contactStatus: ECampaignContactStatus
  followUp: string
  // System prompts are constant for the whole simulation flow, so they are
  // returned once here rather than copied onto every outreach.
  generationSystemPrompt?: string
  evaluationSystemPrompt?: string
}

export type TOfuSimulateContactMessages = {
  responseMessage: string
  timeStamp: string
}

export type TOfuSimulatePayload = {
  campaign: TCampaignCreatePayload
  contact: TCampaignCreateContact
  prevOutreaches: TCreateOutreachPayload[]
  agentId: string
}

export type TOfuSimulatePayloadMessages = {
  agentId: string
  messages: TOfuSimulateMessage[]
  channel: ECampaignChannel
  incomingMessage: string
  contact: TOfuSimulateContact
  campaign: TCampaignCreatePayload
}

export type TCreateOutreachPayloadError = TGenericAPIError

export enum EEmailStatus {
  Sent = 'Sent',
  Delivered = 'Delivered',
  Opened = 'Opened',
  Clicked = 'Clicked',
  Bounced = 'Bounced',
  Complained = 'Complained',
  Rejected = 'Rejected',
}

export type TCampaignEmailMetrics = {
  [EEmailStatus.Sent]: number
  [EEmailStatus.Delivered]: number
  [EEmailStatus.Opened]: number
  [EEmailStatus.Clicked]: number
  [EEmailStatus.Bounced]: number
  [EEmailStatus.Complained]: number
  [EEmailStatus.Rejected]: number
}

export const addSingleContactSchema = yup.object().shape({
  firstName: yup.string(),
  lastName: yup.string(),
  email: yup
    .string()
    .email('Invalid email')
    .test(
      'email-or-phone-required',
      'At least one of email or phone is required',
      function (value) {
        const { phone } = this.parent
        const hasEmail = value && value.trim() !== ''
        const hasPhone = phone && phone.trim() !== ''
        return hasEmail || hasPhone
      },
    ),
  phone: yup
    .string()
    .test(
      'phone-or-email-required',
      'At least one of email or phone is required',
      function (value) {
        const { email } = this.parent
        const hasEmail = email && email.trim() !== ''
        const hasPhone = value && value.trim() !== ''
        return hasEmail || hasPhone
      },
    ),
})

export const populateTestCampaignContactsSchema = yup.object().shape({
  contactsCount: yup
    .number()
    .typeError('Contacts count is required')
    .integer('Contacts count must be an integer')
    .min(1, 'Contacts count must be at least 1')
    .max(100000, 'Contacts count must be less than or equal to 100000')
    .required('Contacts count is required'),
  includeNames: yup.boolean().required(),
  includeEmail: yup.boolean().required(),
  includePhone: yup
    .boolean()
    .required()
    .test(
      'email-or-phone-required',
      'At least one of email or phone must be selected',
      function (value) {
        const { includeEmail } = this.parent
        return Boolean(value || includeEmail)
      },
    ),
  duplicateRate: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value,
    )
    .min(0, 'Duplicate rate must be at least 0')
    .max(1, 'Duplicate rate must be less than or equal to 1')
    .optional(),
  textOptOutRate: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value,
    )
    .min(0, 'Text opt-out rate must be at least 0')
    .max(1, 'Text opt-out rate must be less than or equal to 1')
    .optional(),
  emailUnsubscribedRate: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value,
    )
    .min(0, 'Email unsubscribed rate must be at least 0')
    .max(1, 'Email unsubscribed rate must be less than or equal to 1')
    .optional(),
})

export type TOfuEmailUploadConfigureForm = {
  isEditingEmailConfig: boolean
  isEmailUploadActive: boolean
  userEmailAddress: string
  campaignEmailAddress: string
  firstNameColumn: string
  lastNameColumn: string
  emailColumn: string
  phoneColumn: string
  textOptedOutColumn: string
  emailUnsubscribedColumn: string
  customProperties: TCustomProperty[]
  assistantRouting: TAssistantRoutingConfig
}

export enum EOfuEmailUploadConfigureFormField {
  isEditingEmailConfig = 'isEditingEmailConfig',
  isEmailUploadActive = 'isEmailUploadActive',
  firstNameColumn = 'firstNameColumn',
  lastNameColumn = 'lastNameColumn',
  emailColumn = 'emailColumn',
  phoneColumn = 'phoneColumn',
  textOptedOutColumn = 'textOptedOutColumn',
  emailUnsubscribedColumn = 'emailUnsubscribedColumn',
  customProperties = 'customProperties',
  assistantRoutingMode = 'assistantRouting.mode',
  assistantRoutingAgentId = 'assistantRouting.agentId',
  assistantRoutingExternalIdKey = 'assistantRouting.externalIdKey',
  assistantRoutingExternalIdKeyColumn = 'assistantRouting.externalIdKeyColumn',
  assistantRoutingExternalIdValueColumn = 'assistantRouting.externalIdValueColumn',
}

export const ofuEmailUploadConfigureSchema = yup
  .object()
  .shape({
    userEmailAddress: yup
      .string()
      .email('Your email address is invalid')
      .required('Your email address is required'),
    campaignEmailAddress: yup
      .string()
      .email('Campaign email address is invalid')
      .required('Campaign email address is required'),
    firstNameColumn: yup.string(),
    lastNameColumn: yup.string(),
    emailColumn: yup.string(),
    phoneColumn: yup.string(),
    textOptedOutColumn: yup.string(),
    emailUnsubscribedColumn: yup.string(),
    assistantRouting: yup.object().shape({
      mode: yup
        .string()
        .oneOf(
          Object.values(EAssistantRoutingMode),
          'Assistant routing mode is invalid',
        )
        .required('Assistant routing mode is required'),
      agentId: yup.string().when('mode', {
        is: EAssistantRoutingMode.single,
        then: (schema) => schema.required('Please select an assistant'),
        otherwise: (schema) => schema.optional(),
      }),
      externalIdKey: yup.string().when('mode', {
        is: (mode: EAssistantRoutingMode) =>
          mode === EAssistantRoutingMode.byColumn,
        then: (schema) =>
          schema.required('Assistant external ID key is required'),
        otherwise: (schema) => schema.optional(),
      }),
      externalIdKeyColumn: yup.string().when('mode', {
        is: EAssistantRoutingMode.byKeyAndValueColumns,
        then: (schema) =>
          schema.required('Assistant external ID key column is required'),
        otherwise: (schema) => schema.optional(),
      }),
      externalIdValueColumn: yup.string().when('mode', {
        is: (mode: EAssistantRoutingMode) =>
          mode === EAssistantRoutingMode.byColumn ||
          mode === EAssistantRoutingMode.byKeyAndValueColumns,
        then: (schema) =>
          schema.required('Assistant external ID value is required'),
        otherwise: (schema) => schema.optional(),
      }),
    }),
    customProperties: yup.array().of(
      yup.object().shape({
        id: yup.string().required('ID is required'),
        name: yup.string().required('Name is required'),
        column: yup.string().required('Column is required'),
        customName: yup.string().optional(),
      }),
    ),
  })
  .test(
    'email-or-phone',
    'At least one of email or phone column must be mapped',
    function (value) {
      const { emailColumn, phoneColumn } = value
      return Boolean(emailColumn) || Boolean(phoneColumn)
    },
  )

export enum EUploadLogStatus {
  notStarted = 'notStarted',
  processing = 'processing',
  completed = 'completed',
  failed = 'failed',
  partialFailed = 'partialFailed',
}

export enum EOfuCadence {
  aggressive = 'aggressive',
  moderate = 'moderate',
  balanced = 'balanced',
  relaxed = 'relaxed',
  custom = 'custom',
  // ** Legacy values **
  normal = 'normal',
  soft = 'soft',
}

export enum EUploadLogSourceType {
  apiUpload = 'apiUpload',
  emailCsvUpload = 'emailCsvUpload',
  emailIntegration = 'emailIntegration',
  uiCsvUpload = 'uiCsvUpload',
  uiManualUpload = 'uiManualUpload',
  inboundChannel = 'inboundChannel',
  testCampaignPopulate = 'testCampaignPopulate',
}

export const UploadLogsSourceNames = {
  [EUploadLogSourceType.apiUpload]: 'API request',
  [EUploadLogSourceType.emailCsvUpload]: 'Email CSV',
  [EUploadLogSourceType.emailIntegration]: 'Email integration',
  [EUploadLogSourceType.uiCsvUpload]: 'UI file upload',
  [EUploadLogSourceType.uiManualUpload]: 'UI manual upload',
  [EUploadLogSourceType.inboundChannel]: 'Inbound channel',
  [EUploadLogSourceType.testCampaignPopulate]: 'Populate campaign',
}

export enum ECampaignFiltersFormKeys {
  visibility = 'visibility',
  search = 'search',
  status = 'status',
  channel = 'channel',
  time = 'time',
}

export enum ECampaignFiltersCommonValues {
  all = 'all',
}

export type TCampaignsFiltersForm = {
  [ECampaignFiltersFormKeys.visibility]: ECampaignVisibilityFilter
  [ECampaignFiltersFormKeys.search]: string
  [ECampaignFiltersFormKeys.status]:
    | ECampaignExecutionStatus
    | ECampaignStatus.test
    | ECampaignFiltersCommonValues
  [ECampaignFiltersFormKeys.channel]:
    | ECampaignChannel
    | ECampaignFiltersCommonValues
  [ECampaignFiltersFormKeys.time]:
    | EChartsTimePeriod
    | ECampaignFiltersCommonValues
}

export enum ECampaignVisibilityFilter {
  allCampaigns = 'allCampaigns',
  thisAgent = 'thisAgent',
  sharedOnly = 'sharedOnly',
}

export enum ECampaignsTabs {
  current = 'current',
  archived = 'archived',
}

export enum EPhoneConversationGroups {
  lt15 = 'lt15',
  lt60 = 'lt60',
  lt120 = 'lt120',
  gt120 = 'gt120',
}

export enum EContactsWithResponsesByStatus {
  inProgress = 'inProgress',
  disqualified = 'disqualified',
  completed = 'completed',
}

export type TPhoneCampaignReport = {
  totalPhoneOutreaches: number
  totalPhoneAnsweredOutreaches: number
  totalPhoneVoicemailOutreaches: number
  phoneConversationLength: Record<EPhoneConversationGroups, number>
  totalLinksSent: number
}

export type TChannelDistribution = {
  id: number
  label: string
  value: number
}

export type TAccumulatedContacts = {
  date: string
  contacts: number
}

export type TContactsStatusDistribution = {
  id: number
  label: string
  value: number
}

export type TDailyNumberOfExecutedOutreaches = {
  date: string
  email: number
  phone: number
  text: number
}
export type TContactsWithResponsesByStatus = {
  label: string
  value: number
}

/**
 * Sentiment verdict from the post-completion analysis. `unknown` is a real verdict
 * ("analyzed, could not tell", or the contact never replied); a null/absent sentiment means
 * the analysis has not run for that contact at all.
 */
export enum ECampaignContactSentiment {
  positive = 'positive',
  negative = 'negative',
  neutral = 'neutral',
  unknown = 'unknown',
}

/**
 * Where a contact stands on the campaign goal. goalAchieved alone can't tell "not analyzed" from
 * "not achieved" (older contacts default to false), so a contact counts as analyzed once it has a
 * sentiment, which every analysis writes. Same values as the backend's goalFilter.
 */
export enum ECampaignContactGoalState {
  achieved = 'achieved',
  notAchieved = 'notAchieved',
  notAnalyzed = 'notAnalyzed',
}

export type TCampaignSentimentBreakdown = {
  positive: number
  negative: number
  neutral: number
  unknown: number
  /** Contacts with a sentiment recorded — the exact denominator, not a proxy. */
  analyzed: number
}

export type TCampaignGoalAnalysis = {
  achievedGoal: number
  terminalContacts: number
  /** Every contact on the campaign, finished or not — the denominator the outcome bars span. */
  totalContacts: number
  sentiment?: TCampaignSentimentBreakdown
}

export type TCampaignSummaryReport = {
  channelDistribution: TChannelDistribution[]
  accumulatedContacts: TAccumulatedContacts[]
  contactStatusDistribution: TContactsStatusDistribution[]
  dailyNumberOfExecutedOutreaches: TDailyNumberOfExecutedOutreaches[]
  contactsWithResponsesByStatus: TContactsWithResponsesByStatus[]
  goalAnalysis?: TCampaignGoalAnalysis
}

export type TTextCampaignReport = {
  totalTextOutreaches: number
  optedOutAfterDelivery: number
  repliedWithMeaningfulMessage: number
}

export const PhoneConversationGroupsLabels = {
  [EPhoneConversationGroups.lt15]: '0-15s',
  [EPhoneConversationGroups.lt60]: '15s-1m',
  [EPhoneConversationGroups.lt120]: '1m-2m',
  [EPhoneConversationGroups.gt120]: '2m+',
}

export const ContactsWithResponsesByStatusLabels = {
  [EContactsWithResponsesByStatus.inProgress]: 'In progress',
  [EContactsWithResponsesByStatus.disqualified]: 'Disqualified',
  [EContactsWithResponsesByStatus.completed]: 'Completed',
}

export enum ECampaignTemplateIcon {
  phone = 'phone',
  email = 'email',
  text = 'text',
  gift = 'gift',
  calendar = 'calendar',
  trendingUp = 'trendingUp',
  heart = 'heart',
  bell = 'bell',
  people = 'people',
  custom = 'custom',
  money = 'money',
  campaign = 'campaign',
  emojiPeople = 'emojiPeople',
  connect = 'connect',
  celebration = 'celebration',
  cake = 'cake',
  discount = 'discount',
  emojiEvents = 'emojiEvents',
  rocket = 'rocket',
}

export enum ECampaignTemplateVersionKeys {
  id = 'id',
  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
  version = 'version',
  status = 'status',
  campaignType = 'campaignType',
  editorVariant = 'editorVariant',
  executionMode = 'executionMode',
  displayName = 'displayName',
  description = 'description',
  verticalTags = 'verticalTags',
  searchKeywords = 'searchKeywords',
  campaignGoals = 'campaignGoals',
  firstOutreachTime = 'firstOutreachTime',
  maxReachouts = 'maxReachouts',
  cadence = 'cadence',
  cadenceCustom = 'cadenceCustom',
  channels = 'channels',
  rules = 'rules',
  outreachInstructionsText = 'outreachInstructionsText',
  qualificationCriteria = 'qualificationCriteria',
  campaignFacts = 'campaignFacts',
  mergeFields = 'mergeFields',
  introMessage = 'introMessage',
  voicemailMessage = 'voicemailMessage',
  firstTextMessage = 'firstTextMessage',
  firstEmailBody = 'firstEmailBody',
  firstEmailSubject = 'firstEmailSubject',
  campaignTemplateId = 'campaignTemplateId',
  templateBadge = 'templateBadge',
  activeVersionCampaignTemplate = 'activeVersionCampaignTemplate',
  firstOutreachChannel = 'firstOutreachChannel',
}

export enum ETemplateVersionStatus {
  draft = 'draft',
  active = 'active',
  deprecated = 'deprecated',
}

export enum ETemplateVersionEditorVariant {
  default = 'default',
}

export type TCampaignTemplateVersion = {
  [ECampaignTemplateVersionKeys.id]: string
  [ECampaignTemplateVersionKeys.createdDate]: string
  [ECampaignTemplateVersionKeys.updatedDate]: string
  [ECampaignTemplateVersionKeys.version]: number
  [ECampaignTemplateVersionKeys.status]: ETemplateVersionStatus
  [ECampaignTemplateVersionKeys.campaignType]: ECampaignType
  [ECampaignTemplateVersionKeys.editorVariant]: ETemplateVersionEditorVariant
  [ECampaignTemplateVersionKeys.executionMode]: ECampaignExecutionMode | null
  [ECampaignTemplateVersionKeys.displayName]: string
  [ECampaignTemplateVersionKeys.description]: string | null
  [ECampaignTemplateVersionKeys.verticalTags]: string[] | null
  [ECampaignTemplateVersionKeys.searchKeywords]: string[] | null
  [ECampaignTemplateVersionKeys.campaignGoals]: string | null
  [ECampaignTemplateVersionKeys.firstOutreachTime]: string | null
  [ECampaignTemplateVersionKeys.maxReachouts]: number | null
  [ECampaignTemplateVersionKeys.cadence]: string | null
  [ECampaignTemplateVersionKeys.cadenceCustom]: string | null
  [ECampaignTemplateVersionKeys.channels]: ECampaignChannel[] | null
  [ECampaignTemplateVersionKeys.rules]: string[] | null
  [ECampaignTemplateVersionKeys.outreachInstructionsText]: string | null
  [ECampaignTemplateVersionKeys.qualificationCriteria]: string | null
  [ECampaignTemplateVersionKeys.campaignFacts]: TCampaignFact[] | null
  [ECampaignTemplateVersionKeys.mergeFields]: TMergeFieldWithValue[] | null
  [ECampaignTemplateVersionKeys.introMessage]: string | null
  [ECampaignTemplateVersionKeys.voicemailMessage]: string | null
  [ECampaignTemplateVersionKeys.firstTextMessage]: string | null
  [ECampaignTemplateVersionKeys.firstEmailBody]: string | null
  [ECampaignTemplateVersionKeys.firstEmailSubject]: string | null
  [ECampaignTemplateVersionKeys.campaignTemplateId]: string
  [ECampaignTemplateVersionKeys.templateBadge]?: ECampaignTemplateIcon
  [ECampaignTemplateVersionKeys.activeVersionCampaignTemplate]: TCampaignTemplate | null
  [ECampaignTemplateVersionKeys.campaignGoals]: string | null
  [ECampaignTemplateVersionKeys.firstOutreachChannel]: ECampaignChannel | null
}

export enum ECampaignTemplateOwnerType {
  system = 'system',
  partner = 'partner',
  org = 'org',
  user = 'user',
}

export enum ECampaignTemplateVisibility {
  global = 'global',
  internal = 'internal',
  partner = 'partner',
  org = 'org',
}

export type TCampaignTemplate = {
  id: string
  friendlyName: string
  createdDate: string
  updatedDate: string
  ownerType: ECampaignTemplateOwnerType
  visibility: ECampaignTemplateVisibility
  activeVersionId: string | null
  activeVersion: TCampaignTemplateVersion | null
  campaignTemplateVersions?: TCampaignTemplateVersion[]
  status: ETemplateVersionStatus
}

export enum ECampaignTemplateVersionItemKeys {
  friendlyName = 'friendlyName',
  version = 'version',
  typeAndEditor = 'typeAndEditor',
  channels = 'channels',
  created = 'created',
  actions = 'actions',
}
export type TCampaignTemplateVersionItem = {
  [ECampaignTemplateVersionItemKeys.friendlyName]: string
  [ECampaignTemplateVersionItemKeys.version]: string
  [ECampaignTemplateVersionItemKeys.typeAndEditor]: string
  [ECampaignTemplateVersionItemKeys.channels]: string[]
  [ECampaignTemplateVersionItemKeys.created]: string
  [ECampaignTemplateVersionItemKeys.actions]: string
}

export type TCampaignTemplateFilters = {
  friendlyName?: string
  ownerType?: ECampaignTemplateOwnerType
  visibility?: ECampaignTemplateVisibility
  includeVersions?: boolean
  search?: string
  verticalTags?: string
  includeNotActiveTemplates?: boolean
}

export enum ECampaignTemplatesFormKeys {
  search = 'search',
  campaignName = 'campaignName',
  campaignType = 'campaignType',
  templateId = 'templateId',
  verticalTag = 'verticalTag',
}

export type TCampaignTemplatesForm = {
  [ECampaignTemplatesFormKeys.search]: string
  [ECampaignTemplatesFormKeys.campaignName]: string
  [ECampaignTemplatesFormKeys.campaignType]: ECampaignType
  [ECampaignTemplatesFormKeys.templateId]: string
  [ECampaignTemplatesFormKeys.verticalTag]: string
}

export type TCampaignBuilderParams = {
  id?: string
}

export enum ECampaignBuilderFormKeys {
  agentMetadataKeys = 'agentMetadataKeys',
  agentAccessScope = 'agentAccessScope',
  sharedCampaignAgentIds = 'sharedCampaignAgentIds',
  sharedCampaignAgentSettings = 'sharedCampaignAgentSettings',
  additionalInstructions = 'outreachInstructionsText',
  cadence = 'ofu_cadence',
  cadenceCustom = 'ofu_cadenceCustom',
  channelOfFirstOutreach = 'ofu_firstOutreachChannel',
  contactEligibilityCriteria = 'ofu_qualificationCriteria',
  description = 'description',
  emailContent = 'firstEmailBody',
  emailProvider = 'ofu_outboundEmailProvider',
  emailProviderCustomEmailBody = 'ofu_customEmailBody',
  emailProviderId = 'ofu_outboundEmailProviderId',
  emailSubject = 'firstEmailSubject',
  endDate = 'endDate',
  executionMode = 'executionMode',
  knowledge = 'campaignFacts',
  maxReachouts = 'ofu_maxReachouts',
  mergeFields = 'mergeFields',
  messageStrategy = 'messageStrategy',
  ofuGoal = 'ofu_goal',
  ofuIsManual = 'ofu_isManual',
  ofu_channels = 'ofu_channels',
  ofu_outreachTime = 'ofu_outreachTime',
  phoneIntroMessage = 'introMessage',
  phoneVoicemailMessage = 'voicemailMessage',
  shouldRedirectToManageCampaign = 'shouldRedirectToManageCampaign',
  startDate = 'startDate',
  textMessage = 'firstTextMessage',
  title = 'campaignName',
  useStructuredSchedule = 'useStructuredSchedule',
  structuredTimingMode = 'structuredTimingMode',
  campaignScheduleSteps = 'CampaignScheduleStep',
}

export enum ECampaignExecutionMode {
  scheduled = 'scheduled',
  manual = 'manual',
  onAddContact = 'onAddContact',
}

export enum ECampaignAgentAccessScope {
  singleAgent = 'singleAgent',
  shared = 'shared',
}

export type TSharedCampaignAgentSetting = {
  customKnowledge: string
  customRules: string
  locationFields: Record<string, string>
}

export type TCampaignAgentPayload = {
  agentId: string
  isDeleted?: boolean
  status?: string
  agentCampaignKnowledge?: TCampaignFact[] | null
  agentCampaignRules?: string
  agentMetadata?: Record<string, string>
}

export type TCampaignBuilderForm = {
  [ECampaignBuilderFormKeys.agentMetadataKeys]: string[]
  [ECampaignBuilderFormKeys.agentAccessScope]: ECampaignAgentAccessScope
  [ECampaignBuilderFormKeys.sharedCampaignAgentIds]: string[]
  [ECampaignBuilderFormKeys.sharedCampaignAgentSettings]: Record<
    string,
    TSharedCampaignAgentSetting
  >
  [ECampaignBuilderFormKeys.additionalInstructions]: string
  [ECampaignBuilderFormKeys.cadence]: EOfuCadence
  [ECampaignBuilderFormKeys.cadenceCustom]: string | null
  [ECampaignBuilderFormKeys.channelOfFirstOutreach]: ECampaignChannel | null
  [ECampaignBuilderFormKeys.emailContent]: string
  [ECampaignBuilderFormKeys.emailProvider]: string
  [ECampaignBuilderFormKeys.emailProviderCustomEmailBody]?:
    | string
    | Record<string, unknown>
    | null
  [ECampaignBuilderFormKeys.emailProviderId]: string
  [ECampaignBuilderFormKeys.emailSubject]: string
  [ECampaignBuilderFormKeys.endDate]: string | null
  [ECampaignBuilderFormKeys.contactEligibilityCriteria]: string
  [ECampaignBuilderFormKeys.description]: string
  [ECampaignBuilderFormKeys.executionMode]: ECampaignExecutionMode
  [ECampaignBuilderFormKeys.knowledge]: TCampaignFact[]
  [ECampaignBuilderFormKeys.maxReachouts]: number
  [ECampaignBuilderFormKeys.mergeFields]: TMergeField[]
  [ECampaignBuilderFormKeys.messageStrategy]?: EMessageStrategy | null
  [ECampaignBuilderFormKeys.ofu_channels]: ECampaignChannel[]
  [ECampaignBuilderFormKeys.ofu_outreachTime]: string
  [ECampaignBuilderFormKeys.ofuGoal]: string
  [ECampaignBuilderFormKeys.ofuIsManual]: boolean | null
  [ECampaignBuilderFormKeys.phoneIntroMessage]: string
  [ECampaignBuilderFormKeys.phoneVoicemailMessage]: string
  [ECampaignBuilderFormKeys.shouldRedirectToManageCampaign]: boolean
  [ECampaignBuilderFormKeys.startDate]: string
  [ECampaignBuilderFormKeys.textMessage]: string
  [ECampaignBuilderFormKeys.title]: string
  [ECampaignBuilderFormKeys.useStructuredSchedule]: boolean
  [ECampaignBuilderFormKeys.structuredTimingMode]: EStructuredTimingMode
  [ECampaignBuilderFormKeys.campaignScheduleSteps]: TCampaignScheduleStepForm[]
}

export enum ECampaignBuilderStepIds {
  basics = 'basics',
  multiAssistantSetup = 'multiAssistantSetup',
  deliveryAndTiming = 'deliveryAndTiming',
  behaviorAndRules = 'behaviorAndRules',
  reviewAndSimulate = 'reviewAndSimulate',
}

export enum ECampaignContactsTableColumns {
  check = 'check',
  name = 'name',
  assistant = 'assistant',
  email = 'email',
  phone = 'phone',
  attempts = 'attempts',
  nextAttempt = 'nextAttempt',
  progress = 'progress',
  response = 'response',
  added = 'added',
  status = 'status',
  goal = 'goal',
  sentiment = 'sentiment',
  actions = 'actions',
}

export enum EContactSortBy {
  name = 'name',
  status = 'status',
  progress = 'progress',
  added = 'added',
  email = 'email',
}

export enum EContactSortDirection {
  asc = 'asc',
  desc = 'desc',
}

// Validation schema for outreach form
export const outreachFormValidationSchema = yup.object({
  emailSubject: yup
    .string()
    .max(191, 'Subject must be less than 191 characters')
    .when('channel', {
      is: ECampaignChannel.email,
      then: (schema) => schema.required('Email subject is required'),
      otherwise: (schema) => schema.optional(),
    }),
  emailBody: yup
    .string()
    .max(2000, 'Email body must be less than 2000 characters')
    .when('channel', {
      is: ECampaignChannel.email,
      then: (schema) => schema.required('Email body is required'),
      otherwise: (schema) => schema.optional(),
    }),
  callScript: yup
    .string()
    .max(1000, 'Call script must be less than 1000 characters')
    .when('channel', {
      is: ECampaignChannel.phone,
      then: (schema) => schema.required('Call script is required'),
      otherwise: (schema) => schema.optional(),
    }),
  voicemailScript: yup
    .string()
    .max(1000, 'Voicemail script must be less than 1000 characters')
    .optional(),
  textScript: yup
    .string()
    .max(1200, 'Text script must be less than 1200 characters')
    .when('channel', {
      is: ECampaignChannel.text,
      then: (schema) => schema.required('Text script is required'),
      otherwise: (schema) => schema.optional(),
    }),
  channel: yup
    .string()
    .oneOf(Object.values(ECampaignChannel), 'Invalid channel')
    .required('Channel is required'),
})

export type TOutreachFormValues = yup.InferType<
  typeof outreachFormValidationSchema
>
